import React, { useCallback } from 'react';
import {
  Button,
  Center,
  Flex,
  Icon,
  IconButton,
  icons,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Stack,
  Text,
} from '@tapresearch/ui/components';
import {
  ComparableTapQuestionDetails,
  DemographicQuestionDetails,
} from './SurveyResultsWrapper';

interface QuestionItemComparisonsProps {
  comparisonQuestions: [
    Record<string, DemographicQuestionDetails>,
    Record<string, ComparableTapQuestionDetails>
  ];
  compareState: [undefined | string, (val: string) => void];
  tapQuestionId: string;
}
export const QuestionItemComparisons = ({
  comparisonQuestions: [demographicQuestions, comparableTapQuestions],
  compareState: [crossTabQuestionId = null, setCrossTabQuestionId],
  tapQuestionId,
}: QuestionItemComparisonsProps) => {
  const handleChangeCrossTab = useCallback(
    (val: string) => setCrossTabQuestionId(val),
    [setCrossTabQuestionId]
  );

  return (
    <Stack
      {...{
        direction: { base: 'column', md: 'row' },
        w: 'full',
        maxW: { base: 'none', md: 'calc(100% - 260px)' },
        spacing: 4,
        flexGrow: 1,
      }}
    >
      <CompareSelect
        demographicQuestions={demographicQuestions}
        onChange={handleChangeCrossTab}
        currentValue={crossTabQuestionId}
      />
      <CompareSelect
        tapQuestions={comparableTapQuestions}
        tapQuestionId={tapQuestionId}
        onChange={handleChangeCrossTab}
        currentValue={crossTabQuestionId}
      />
    </Stack>
  );
};

interface CompareSelectProps {
  demographicQuestions?: Record<string, DemographicQuestionDetails>;
  tapQuestions?: Record<string, ComparableTapQuestionDetails>;
  currentValue?: false | string;
  isDisabled?: boolean;
  tapQuestionId?: string;
  onChange: (val: string) => void;
}
const CompareSelect = ({
  demographicQuestions,
  tapQuestions,
  currentValue,
  onChange,
  tapQuestionId,
  isDisabled = false,
}: CompareSelectProps) => {
  const isDemographic = !!demographicQuestions;
  const questions = isDemographic ? demographicQuestions : tapQuestions;
  const activeComparison = currentValue && questions[currentValue];

  const onClear = useCallback(
    (e) => {
      e.stopPropagation();
      onChange('');
    },
    [onChange]
  );

  return (
    <Menu matchWidth autoSelect={false}>
      <MenuButton
        {...{
          isDisabled,
          as: Button,
          flexGrow: 1,
          minW: '200px',
          variant: 'outline',
          colorScheme: 'gray',
          color: 'gray.700',
          maxW: { base: 'none', md: 'calc(50% - 20px)' },
          _hover: {
            bg: 'blue.50',
          },
          rightIcon: (
            <Icon color="gray.600" boxSize={5} icon={icons.fi.ChevronDown} />
          ),
          ...(activeComparison && {
            borderColor: 'blue.500',
            bg: 'blue.50',
          }),
        }}
      >
        <Flex w="full" align="center" justify="space-between">
          <Text
            textAlign="left"
            lineHeight="normal"
            isTruncated
            w="calc(100% - 30px)"
          >
            {activeComparison?.name ||
              (isDemographic
                ? 'Compare against demographic'
                : 'Compare against Question')}
          </Text>
          {activeComparison && (
            <IconButton
              {...{
                'aria-label': 'Remove current comparison',
                as: Center,
                variant: 'square',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                size: 5 as any,
                p: 1,
                color: 'gray.600',
                position: 'absolute',
                onClick: onClear,
                right: '40px',
                pointerEvents: 'all',
                zIndex: '2',
                _hover: {
                  bg: 'error.100',
                  color: 'error.600',
                },
                icon: <Icon boxSize={5} icon={icons.fi.X} />,
              }}
            />
          )}
        </Flex>
      </MenuButton>
      <MenuList>
        <MenuOptionGroup value={currentValue || null} type="radio">
          {Object.entries(questions || {}).map(([questionId, question]) => {
            if (questionId === `tq_${tapQuestionId}`) return null;
            return (
              <MenuItemOption
                key={questionId}
                value={questionId}
                flexWrap="wrap"
                isChecked={questionId === currentValue}
                onClick={() => onChange(questionId)}
                sx={{
                  'span:nth-of-type(2)': {
                    width: '0',
                  },
                }}
              >
                {question.name}
              </MenuItemOption>
            );
          })}
        </MenuOptionGroup>
      </MenuList>
    </Menu>
  );
};
